import React from 'react';
import { Link } from 'react-router-dom';
type ButtonVariant = 'primary' | 'secondary' | 'destructive' | 'outline' | 'ghost' | 'accent' | 'patriot';
type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  href?: string;
  fullWidth?: boolean;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}
export function Button({
  children,
  variant = 'primary',
  size = 'md',
  href,
  fullWidth = false,
  isLoading = false,
  className = '',
  disabled,
  leftIcon,
  rightIcon,
  ...props
}: ButtonProps) {
  const baseStyles = 'inline-flex items-center justify-center rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none touch-manipulation active:scale-[0.98] tracking-wide';
  const variants = {
    primary: 'bg-primary-900 text-white hover:bg-primary-800 shadow-sm hover:shadow-md focus:ring-primary-900 border border-transparent',
    accent: 'bg-accent-600 text-white hover:bg-accent-700 shadow-sm hover:shadow-md focus:ring-accent-600 border border-transparent',
    patriot: 'bg-gradient-to-r from-primary-900 to-primary-800 text-white hover:from-primary-800 hover:to-primary-700 shadow-md hover:shadow-lg border-b-2 border-accent-600',
    secondary: 'bg-white text-primary-900 hover:bg-slate-50 shadow-sm border border-slate-200 focus:ring-slate-200',
    destructive: 'bg-destructive text-white hover:bg-red-800 shadow-sm focus:ring-destructive border border-transparent',
    outline: 'bg-transparent border border-slate-300 text-slate-700 hover:border-primary-900 hover:text-primary-900 hover:bg-slate-50 focus:ring-primary-900',
    ghost: 'bg-transparent text-slate-600 hover:bg-slate-100 hover:text-primary-900 focus:ring-slate-200'
  };
  const sizes = {
    sm: 'min-h-[36px] px-4 py-1.5 text-xs',
    md: 'min-h-[44px] px-6 py-2.5 text-sm',
    lg: 'min-h-[52px] px-8 py-3 text-base',
    xl: 'min-h-[60px] px-10 py-4 text-lg'
  };
  const widthStyles = fullWidth ? 'w-full' : '';
  const classes = `${baseStyles} ${variants[variant]} ${sizes[size]} ${widthStyles} ${className}`;
  const content = <>
      {isLoading && <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-44">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-45"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-46"></path>
        </svg>}
      {!isLoading && leftIcon && <span className="mr-2" data-id="element-47">{leftIcon}</span>}
      {isLoading ? 'Loading...' : children}
      {!isLoading && rightIcon && <span className="ml-2" data-id="element-48">{rightIcon}</span>}
    </>;
  if (href) {
    return <Link to={href} className={classes} data-id="element-49">
        {content}
      </Link>;
  }
  return <button className={classes} disabled={disabled || isLoading} {...props} data-id="element-50">
      {content}
    </button>;
}